<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <Database class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Custom storage
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent>
        <div class="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <button
                v-for="provider in visibleProviders"
                :key="provider.key"
                class="flex flex-col items-center bg-new-neutral-50 hover:bg-new-neutral-100 p-4 transition hover:bg-new-neutral-50"
                @click="$emit('selectProvider', provider.key)"
            >
                <div class="mb-2 flex h-8 items-center justify-center">
                    <img
                        :src="provider.img"
                        :alt="provider.name"
                        class="object-contain"
                        :style="figmaSize(provider.key)"
                    />
                </div>
                <p class="text-center text-[9px] leading-4 font-semibold text-new-blue-500">
                    {{ apiToken.settings?.storage_provider ? `Edit ${provider.name} storage` : `Set up ${provider.name} storage` }}
                </p>
            </button>
        </div>

        <p class="text-[10px] leading-4 text-muted-foreground">
            <span v-if="!apiToken?.settings?.storage_provider">
                Currently using default storage. Configure your own Google Cloud, Amazon S3, or Cloudflare R2.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'gcs'">
                Using custom Google Cloud storage. Detach current storage to switch providers.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'aws'">
                Using Amazon S3 storage. Detach current storage to switch providers.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'cloudflare'">
                Using Cloudflare R2 storage. Detach current storage to switch providers.
            </span>
            For more information,
            <a
                href="https://docs.topol.io"
                target="_blank"
                class="font-semibold underline text-new-blue-500 hover:text-new-blue-600"
            >
                check out our documentation</a
            >.
        </p>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { PropType, computed } from 'vue';
import { Database } from 'lucide-vue-next';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import IToken from '@/types/Plugin/IToken';
import { allProviders } from '@/Pages/PluginSettings/data/storageProviders';

const props = defineProps({
    apiToken: {
        type: Object as PropType<IToken>,
        required: true,
    },
});

defineEmits<{
    selectProvider: [provider: 'aws' | 'cloudflare' | 'gcs'];
}>();

const visibleProviders = computed(() => {
    if (!props.apiToken?.settings?.storage_provider) {
        return allProviders;
    }

    return allProviders.filter(
        (p) => p.key === props.apiToken?.settings?.storage_provider
    );
});

// Per-provider sizes from the v2 Figma (smaller than the v1 sizes carried in storageProviders.ts).
const figmaSize = (key: 'gcs' | 'aws' | 'cloudflare') => {
    switch (key) {
        case 'gcs':
            return { width: '102px', height: '32px' };
        case 'aws':
            return { width: '32px', height: '32px' };
        case 'cloudflare':
            return { width: '73px', height: '24px' };
    }
};
</script>
